import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { AccountingEventType, ContractType } from '@received/pricing-model';
import { Button, Modal, SearchBar, Select } from '../_uiComponents';
import { PricingModel } from '../../types/contractTypes';
import { EmptyCatalogPreview, PricingList, PricingTableCardView } from '..';
import { ProductAndCategoriesItem, ProductsHeaderId } from '../../types/ProductTypes';
import { successErrorMassageOptions } from '../SuccessErrorModal/SuccessErrorModal.utils';
import { httpService, PathsConfig } from '../../services';
import { setOpenSuccessErrorModal } from '../../storeSlices/errorSuccessSlice';
import { onClickSelectOrEdit } from './CatalogModal.utils';
import { filterCatalogPricingModels, pricingFilter, pricingSelectList } from '../../pages/PricingModels/PricingModels.utils';
import { PricingTable } from '../../pages/PricingModels/PricingModelTabs/MyProductsTab/PricingTable/PricingTable';
import { getTemplateColorAndIconById } from '../../pages/PricingModels/PricingModelTabs/AllTemplatesTab/AllTemplatesTab.utils';
import { PricingModelTableViewTypeOptions } from '../PricingModelTable/PricingModelTable.utils';
import styles from './CatalogModalV2.module.scss';

// Dedicated fork of CatalogModal for the Contract V2 flow (mirrors ContractContentV2). Differs from V1:
// preview table uses the stigg-style look (isV2), and every row defaults to selected.
interface CatalogModalV2Props {
	openModal: boolean;
	selectedModel?: PricingModel;
	editMode?: boolean;
	contractType?: ContractType;
	setOpenModal: (value: boolean) => void;
	onUseThisPricing?: (model: PricingModel) => void;
	onProductsChange?: (productId?: string) => void;
	addNewPricingEmptyProduct?(selectedProduct: ProductAndCategoriesItem): void;
}

export const CatalogModalV2 = ({
	openModal,
	selectedModel,
	editMode,
	contractType,
	setOpenModal,
	onUseThisPricing,
	addNewPricingEmptyProduct,
}: CatalogModalV2Props) => {
	const { t } = useTranslation('translation');
	const [myProductsList, setMyProductsList] = useState<ProductAndCategoriesItem[]>([]);
	const [pricingTemplateList, setPricingTemplateList] = useState<ProductAndCategoriesItem[]>([]);
	const [selectedProduct, setSelectedProduct] = useState<ProductAndCategoriesItem>();
	const [selectedPricingModel, setSelectedPricingModel] = useState<PricingModel>();
	const [searchText, setSearchText] = useState('');
	const [selectedFilter, setSelectedFilter] = useState<pricingFilter | ''>(pricingFilter.isFavorite);
	const [listOfSelectedRows, setListOfSelectedRows] = useState<boolean[][]>([]);
	const [disableUseButton, setDisableUseButton] = useState(editMode);
	const [isMainTableSwitchOn, setIsMainTableSwitchOn] = useState<boolean>(true);

	const disableUseThisPricing = !onUseThisPricing || !selectedPricingModel || disableUseButton;

	const dispatch = useDispatch();

	useEffect(() => {
		if (openModal) {
			contractType !== ContractType.REFERRAL && setCategories();
			setMyProducts();
		}
	}, [openModal]);

	useEffect(() => {
		// V2 always defaults to every row selected ("Select all rows" on).
		const selectedModelRows = selectedPricingModel?.tabs?.map((model) => model.pricingModelTable.rows.map(() => true));
		setIsMainTableSwitchOn(true);
		selectedPricingModel?.id && setListOfSelectedRows(selectedModelRows || []);
	}, [selectedPricingModel?.tabs]);

	useEffect(() => {
		if (listOfSelectedRows?.length && editMode) {
			const checker = listOfSelectedRows[0].every((v) => !v);
			setDisableUseButton(checker);
		}
	}, [listOfSelectedRows]);

	const setMyProducts = async () => {
		try {
			let productList: ProductAndCategoriesItem[] = (
				await httpService({
					dispatch,
					path: PathsConfig.myProductsList,
				})
			).data;

			if (contractType === ContractType.REFERRAL) {
				productList = productList.map((product) => ({
					...product,
					pricingModels: product.pricingModels.filter((model) => model.tabs[0].accountingEventType === AccountingEventType.PAYOUT),
				}));
			} else {
				productList = productList.map((product) => ({
					...product,
					pricingModels: product.pricingModels.filter((model) => model.tabs[0].accountingEventType != AccountingEventType.PAYOUT),
				}));
			}

			setMyProductsList(productList);

			if (selectedModel) {
				setSelectedPricingModel(selectedModel);
				const foundProduct = productList.find((product) => {
					return !!product.pricingModels.filter((model) => model.id === selectedModel.id).length;
				});
				// Only set the product for the user's own models; for a template, leave it so setCategories can
				// point at the "All Templates" pane instead of clobbering it with undefined (empty modal).
				if (foundProduct) {
					setSelectedProduct(foundProduct);
				}
			} else if (contractType === ContractType.REFERRAL) {
				// Referral has no templates, so the (hidden-pane) V2 modal defaults to the payout products list.
				onSelectMyProducts(productList);
			}
		} catch (error) {
			dispatch(setOpenSuccessErrorModal({ responseError: error, successErrorMassage: successErrorMassageOptions.ERROR.FAILED_LOAD_PRODUCTS }));
		}
	};

	const setCategories = async () => {
		try {
			const templates: ProductAndCategoriesItem[] = (
				await httpService({
					dispatch,
					path: PathsConfig.getCategoriesList,
					params: { includePricingModels: 'true' },
				})
			).data;

			const models = templates.map((product) => {
				const models = product.pricingModels.map((model) => ({
					...model,
					iconType: getTemplateColorAndIconById(product.id).iconType,
					categoryName: product.name,
				}));

				return {
					...product,
					iconType: getTemplateColorAndIconById(product.id).iconType,
					count: product.pricingModels.length,
					pricingModels: [...models],
				};
			});

			setPricingTemplateList(models);
			// V2 opens on "All Templates" (products pane hidden) unless a specific model was passed in.
			if (!selectedModel) {
				onSelectTemplates(models);
			} else {
				// If the passed model is a template, surface the "All Templates" pane so the list/header render;
				// don't overwrite the selectedPricingModel setMyProducts already set from selectedModel.
				const templateModels = models.map((product) => product.pricingModels || []).flat();
				if (templateModels.some((model) => model.id === selectedModel.id)) {
					setSelectedProduct({ id: ProductsHeaderId.CATEGORIES, name: t('allTemplates'), pricingModels: templateModels });
				}
			}
		} catch (error) {
			dispatch(setOpenSuccessErrorModal({ responseError: error, successErrorMassage: successErrorMassageOptions.ERROR.FAILED_LOAD_CATEGORIES }));
		}
	};

	const onSelectMyProducts = (products: ProductAndCategoriesItem[]) => {
		const models = products?.map((product) => product?.pricingModels || [])?.flat();
		const myProductsAsProduct = {
			id: ProductsHeaderId.PRODUCTS,
			name: t('allProducts'),
			pricingModels: models,
		};
		setSelectedPricingModel(models.length ? models[0] : undefined);
		setSelectedProduct(myProductsAsProduct);
	};

	const onSelectTemplates = (templates: ProductAndCategoriesItem[]) => {
		const models = templates?.map((product) => product?.pricingModels || [])?.flat();

		const templatesAsProduct = {
			id: ProductsHeaderId.CATEGORIES,
			name: t('allTemplates'),
			pricingModels: models,
		};
		setSelectedProduct(templatesAsProduct);
		setSelectedPricingModel(models.length ? models[0] : undefined);
	};

	const isProduct = (productSelected?: ProductAndCategoriesItem) => {
		return productSelected && !(pricingTemplateList?.includes(productSelected) || productSelected.id === ProductsHeaderId.CATEGORIES);
	};

	const onClose = () => {
		setSelectedProduct(undefined);
		setSelectedPricingModel(undefined);
		setOpenModal(false);
	};

	const onUpdateSelectedRows = (rowIndex: number, isSelected: boolean) => {
		const newList = [...listOfSelectedRows];
		newList[0] = [...listOfSelectedRows[0]];
		newList[0][rowIndex] = isSelected;

		setListOfSelectedRows(newList);
	};

	const onAllUpdateSelectedRows = (isSelected: boolean) => {
		if (selectedPricingModel) {
			const selectedModelRows = selectedPricingModel?.tabs?.map((model) => model.pricingModelTable.rows.map(() => isSelected));
			selectedPricingModel?.id && setListOfSelectedRows(selectedModelRows);
		}
	};

	return (
		<Modal isOpen={openModal} closeModal={onClose}>
			<div data-testid='products-catalog-modal' className={styles.mainContainer}>
				<div className={styles.rightContainer}>
					{isProduct(selectedProduct) ? (
						selectedPricingModel ? (
							<PricingTable
								customHeader={
									<div className={styles.pricingTableHeader}>
										<div>{selectedProduct?.name}</div>
										<Select
											fullBorder
											width={12}
											data={pricingSelectList(t)}
											disabled={!isProduct(selectedProduct)}
											defaultValue={selectedFilter}
											onChange={(selected: pricingFilter) => setSelectedFilter(selected)}
										/>
									</div>
								}
								selectedRows={listOfSelectedRows}
								list={filterCatalogPricingModels(selectedFilter, selectedProduct?.pricingModels || [], searchText)}
								selectedModel={selectedPricingModel}
								selectPricing={(model) => setSelectedPricingModel(model)}
								selectButtonText='useThisPricing'
								isV2
								pricingModelTableViewType={
									editMode ? PricingModelTableViewTypeOptions.CATALOG_PREVIEW_EDITABLE : PricingModelTableViewTypeOptions.CATALOG_PREVIEW_NOT_EDITABLE
								}
								onUseThisPricing={() => onClickSelectOrEdit(selectedPricingModel, listOfSelectedRows, onClose, onUseThisPricing)}
								onUpdateSelectedRows={onUpdateSelectedRows}
								disableUseThisPricing={disableUseThisPricing}
								onRefreshTable={() => setMyProducts()}
								showSwitch={{
									// V2 hides the "Select all rows" switch (rows are always all-selected).
									isVisible: false,
									onChange: (isSelected) => onAllUpdateSelectedRows(isSelected),
									defaultChecked: isMainTableSwitchOn,
								}}
							/>
						) : (
							<div>
								<div className={styles.pricingTableHeader}>
									<div>{selectedProduct?.name}</div>
								</div>
								<EmptyCatalogPreview
									addNewPricing={addNewPricingEmptyProduct && selectedProduct ? () => addNewPricingEmptyProduct?.(selectedProduct) : undefined}
								/>
							</div>
						)
					) : (
						<div className={styles.templateListContainer}>
							<PricingList
								customHeader={
									<div className={styles.pricingTableHeader}>
										<div>{selectedProduct?.name}</div>
									</div>
								}
								searchBar={<SearchBar onChange={setSearchText} className={styles.searchBar} placeholder={`${t('search')}...`} />}
								nameColumnTitle={t('name')}
								list={filterCatalogPricingModels('', selectedProduct?.pricingModels || [], searchText)}
								selectedModel={selectedPricingModel}
								selectPricing={(model) => setSelectedPricingModel(model)}
								listContainerClassName={styles.pricingList}
								className={styles.pricingListContainer}
							/>
							<div className={styles.tableContainer}>
								<PricingTableCardView
									showSwitch={{
										// V2 hides the "Select all rows" switch (rows are always all-selected).
										isVisible: false,
										onChange: (isSelected) => onAllUpdateSelectedRows(isSelected),
										defaultChecked: isMainTableSwitchOn,
									}}
									pricingModel={selectedPricingModel}
									selectedRows={listOfSelectedRows}
									isV2
									imgType={selectedPricingModel?.iconType || 'subscription_template'}
									tagText={selectedPricingModel?.categoryName}
									pricingModelTableViewType={
										editMode
											? PricingModelTableViewTypeOptions.CATALOG_PREVIEW_EDITABLE
											: PricingModelTableViewTypeOptions.CATALOG_PREVIEW_NOT_EDITABLE
									}
									onUpdateSelectedRows={onUpdateSelectedRows}
								/>

								<div className={styles.buttonsContainer}>
									<Button
										dataTestId='edit-pricing'
										onClick={() => onClickSelectOrEdit(selectedPricingModel, listOfSelectedRows, onClose, onUseThisPricing)}
										disabled={disableUseThisPricing}
									>
										{t('useThisPricing')}
									</Button>
								</div>
							</div>
						</div>
					)}
				</div>
			</div>
		</Modal>
	);
};
